import { Typography } from "@mui/material";
import { Fragment } from "react";
import { useSelector } from "react-redux";
import { frequencyEnum, mealTypesEnum } from "../../../util/enum";
import { getRightPackageUom } from "../../../util/function";

const COL_COUNT = 7;

const toSafeText = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};

const PrescriptionView = ({ prescriptions, showShortName = false }) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const { clinicStocks } = useSelector((state) => state?.clinic);

  const getBatchList = (stockId = null) => {
    if (!stockId) return [];
    const stocks = Array.isArray(clinicStocks?.data) ? clinicStocks.data : [];
    const reqStock = stocks.find((stc) => stc?.stockId === stockId);
    return Array.isArray(reqStock?.stockBatch) ? reqStock.stockBatch : [];
  };

  const getNameWithValue = (itm) => {
    const shortName = toSafeText(itm?.shortName);
    if (shortName.length > 0) return shortName;
    const name = toSafeText(itm?.name);
    if (name.length > 0) return name;
    const plName = toSafeText(itm?.plName);
    if (plName.length > 0) return plName;
    return "";
  };

  const getFrequencyLabel = (li) => {
    try {
      if (
        Array.isArray(li?.frequencyDuration) &&
        li.frequencyDuration.length > 0
      ) {
        return li.frequencyDuration
          .map((key) => {
            const safeKey = toSafeText(key);
            if (!safeKey) return "";
            return frequencyEnum?.[safeKey] ?? safeKey;
          })
          .filter(Boolean)
          .join(", ");
      }
      if (typeof li?.frequencyDuration === "string") {
        return li.frequencyDuration;
      }
      if (Array.isArray(li?.frequency) && li.frequency.length > 0) {
        return li.frequency
          .map((fre) => {
            if (typeof fre === "string") {
              return frequencyEnum?.[fre] ?? fre;
            }
            if (fre && typeof fre === "object") {
              const apiValue = toSafeText(fre?.apiValue);
              return (
                (apiValue ? frequencyEnum?.[apiValue] : null) ??
                toSafeText(fre?.name) ??
                toSafeText(fre?.value) ??
                ""
              );
            }
            return "";
          })
          .filter(Boolean)
          .join(", ");
      }
      return `${li?.morning ? "1" : "0"}-${li?.afternoon ? "1" : "0"}-${
        li?.night ? "1" : "0"
      }`;
    } catch {
      return "";
    }
  };

  const getDoseLabel = (li) => {
    try {
      const batchList = getBatchList(li?.stockId);
      const packageUom = li?.packageUom
        ? (batchList.find((bat) => bat?.batchId === li?.batchId)?.packageUom ??
          "")
        : (batchList?.[0]?.packageUom ?? "");
      const uomLabel = getRightPackageUom?.(packageUom, true);
      return `${toSafeText(li?.dosage)}${toSafeText(uomLabel)}`;
    } catch {
      return toSafeText(li?.dosage);
    }
  };

  const getRouteLabel = (route) => {
    if (route == null) return "";
    if (typeof route === "string" || typeof route === "number") {
      return toSafeText(route);
    }
    if (typeof route === "object") {
      return toSafeText(route?.name) || toSafeText(route?.value);
    }
    return "";
  };

  const getMealLabel = (meal) => {
    const mealKey = toSafeText(meal);
    if (!mealKey) return "";
    return toSafeText(mealTypesEnum?.[mealKey]);
  };

  const getNotes = (pres) => {
    const notes = pres?.remark ?? pres?.remarks ?? "";
    return toSafeText(notes);
  };

  const getDetailsList = (pres) => {
    const details = pres?.prescriptionDetails;
    if (!Array.isArray(details)) return [];
    return details.filter((item) => item != null && typeof item === "object");
  };

  const prescriptionList = (
    Array.isArray(prescriptions)
      ? prescriptions
      : Array.isArray(reqDet?.prescription)
        ? reqDet.prescription
        : []
  ).filter((pres) => pres != null && typeof pres === "object");

  const hasAnyDetails = prescriptionList.some(
    (pres) => getDetailsList(pres).length > 0,
  );
  const hasAnyNotes = prescriptionList.some(
    (pres) => getNotes(pres).trim().length > 0,
  );

  if (!hasAnyDetails && !hasAnyNotes) {
    return <div className="prescription-print-table" />;
  }

  return (
    <div className="prescription-print-table">
      <table className="prescription-print-native-table">
        <colgroup>
          {/* Same column widths as WorkBookHistory print (App.css prescription-print-col-*) */}
          <col className="prescription-print-col-name" style={{ width: "25%" }} />
          <col className="prescription-print-col-dose" style={{ width: "12%" }} />
          <col className="prescription-print-col-freq" style={{ width: "13%" }} />
          <col className="prescription-print-col-route" style={{ width: "10%" }} />
          <col
            className="prescription-print-col-duration"
            style={{ width: "10%" }}
          />
          <col
            className="prescription-print-col-instructions"
            style={{ width: "15%" }}
          />
          <col
            className="prescription-print-col-remarks"
            style={{ width: "15%" }}
          />
        </colgroup>
        {hasAnyDetails ? (
          <thead>
            <tr>
              <th>
                <Typography className="fs12 txt-semi-bold black">Name</Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">Dose</Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">
                  Frequency
                </Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">Route</Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">
                  Duration
                </Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">
                  Instructions
                </Typography>
              </th>
              <th>
                <Typography className="fs12 txt-semi-bold black">
                  Remarks
                </Typography>
              </th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {prescriptionList.map((pres, i) => {
            const reqProcedDetList = [...getDetailsList(pres)].sort(
              (a, b) =>
                (Number(a?.id ?? a?.detailId) || 0) -
                (Number(b?.id ?? b?.detailId) || 0),
            );
            const notes = getNotes(pres);
            const noteLines = notes.split("\n");

            return (
              <Fragment key={`pres_group_${i}`}>
                {reqProcedDetList.map((li, rowIdx) => (
                  <tr key={`rec_${li?.id ?? li?.i ?? `${i}_${rowIdx}`}`}>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {showShortName
                          ? getNameWithValue(li)
                          : toSafeText(li?.name) || toSafeText(li?.plName)}
                      </Typography>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {getDoseLabel(li)}
                      </Typography>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {getFrequencyLabel(li)}
                      </div>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {getRouteLabel(li?.route)}
                      </Typography>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {toSafeText(li?.duration) || "1"} days
                      </Typography>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {getMealLabel(li?.meal)}
                      </div>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {toSafeText(li?.remark)}
                      </div>
                    </td>
                  </tr>
                ))}
                {notes.trim().length > 0 ? (
                  <tr className="prescription-print-notes-row">
                    <td
                      className="prescription-print-notes"
                      colSpan={COL_COUNT}
                    >
                      <Typography className="fs12 txt-semi-bold black mt10">
                        Notes
                      </Typography>
                      <Typography
                        className="fs12 txt-regular black"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                        component="div"
                      >
                        {noteLines.map((line, index) => (
                          <div key={index}>{line || "\u00A0"}</div>
                        ))}
                      </Typography>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PrescriptionView;
